import { DriverAuthenticationManager } from '@frontend/authentication-v2';
import { EntityType } from '@frontend/common';
import { OrderClient } from '@frontend/order/api';
import { OrderStatusName } from '@frontend/order/types';
import { IProduct } from '@frontend/product/types';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { SpotExtendedClient } from '@frontend/spot/api';
import { SpotExtended } from '@frontend/spot/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useScreen } from '../useScreen';

export type DeliverySpot = Pick<SpotExtended, 'id' | 'name'>;
type DeliveryLocationOption = 'current' | 'default' | 'other' | null;

export const useSelectOrderDeliveryLocation = () => {
    const { navigationState, onBack, onSubmit, store, updateWizardData } = useScreen();
    const selectedProduct = (store?.data?.selectedProduct as IProduct | null | undefined) ?? null;
    const selectedAmount = store?.data?.selectedAmount ?? 1;
    const [defaultSpot, setDefaultSpot] = useState<SpotExtended | null>(null);
    const [selectedSpot, setSelectedSpot] = useState<DeliverySpot | null>(null);
    const [selectedAnotherSpot, setSelectedAnotherSpot] = useState<DeliverySpot | null>(null);
    const [selectedDeliveryOption, setSelectedDeliveryOption] = useState<DeliveryLocationOption>(null);
    const [isLoadingDefaultSpot, setIsLoadingDefaultSpot] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const currentSpot = useMemo<DeliverySpot | null>(
        () => (navigationState.spot ? { id: navigationState.spot.id, name: navigationState.spot.name } : null),
        [navigationState.spot]
    );

    useEffect(() => {
        if (currentSpot && !selectedSpot) {
            setSelectedSpot(currentSpot);
            setSelectedDeliveryOption('current');
        }
    }, [currentSpot, selectedSpot]);

    useEffect(() => {
        const accountId = navigationState.user?.accountId;
        const userId = navigationState.user?.id;
        if (!accountId || !userId) {
            setIsLoadingDefaultSpot(false);
            return;
        }

        let isMounted = true;
        const fetchDefaultSpot = async () => {
            try {
                const settings = await SettingClient.fetchSettings({ entity_type: EntityType.USER, entity_id: userId, key: SettingKey.DEFAULT_SPOT_ID });
                const defaultSpotId = settings.results.find((setting) => setting.key === SettingKey.DEFAULT_SPOT_ID)?.value;
                if (typeof defaultSpotId !== 'string') return;

                const spot = await SpotExtendedClient.fetchSpotExtended(accountId, defaultSpotId);
                if (!isMounted) return;
                setDefaultSpot(spot);
                if (!currentSpot) {
                    setSelectedSpot(spot);
                    setSelectedDeliveryOption('default');
                }
            } catch {
                // A default location is optional; the user can still choose another SPOT.
            } finally {
                if (isMounted) setIsLoadingDefaultSpot(false);
            }
        };

        fetchDefaultSpot();
        return () => {
            isMounted = false;
        };
    }, [currentSpot, navigationState.user?.accountId, navigationState.user?.id]);

    const selectCurrentSpot = () => {
        if (!currentSpot) return;
        setSelectedSpot(currentSpot);
        setSelectedAnotherSpot(null);
        setSelectedDeliveryOption('current');
    };

    const selectDefaultSpot = () => {
        if (!defaultSpot) return;
        setSelectedSpot(defaultSpot);
        setSelectedAnotherSpot(null);
        setSelectedDeliveryOption('default');
    };

    const selectAnotherSpot = (spot: DeliverySpot) => {
        setSelectedSpot(spot);
        setSelectedAnotherSpot(spot);
        setSelectedDeliveryOption('other');
    };

    const confirmOrder = useCallback(async () => {
        const accountId = store?.wizard.account_id;
        const user = navigationState.user;
        if (!accountId || !user || !selectedProduct || !selectedSpot) return;

        setIsSubmitting(true);
        try {
            const iotToken = await DriverAuthenticationManager.getInstance().waitForToken();
            const configuredOrderWorkflowId = store?.functionalitySettings.find((setting) => setting.key === SettingKey.ORDER_WORKFLOW_ID)?.value;
            await OrderClient.postOrder(
                accountId,
                {
                    user_id: user.id,
                    spot_id: selectedSpot.id,
                    product_id: selectedProduct.id,
                    quantity: selectedAmount,
                    state: OrderStatusName.PENDING,
                    workflow_id: typeof configuredOrderWorkflowId === 'string' ? configuredOrderWorkflowId : null
                },
                iotToken
            );
            updateWizardData({
                selectedLeafCategory: null,
                browseProductsNavigation: null,
                selectedProduct: null,
                selectedProductStocks: [],
                selectedAmount: 1,
                selectedAllowance: null,
                selectedCertification: null,
                selectedProductCanBeOrdered: false,
                orderSpotId: null,
                orderSpotName: null
            });
            onSubmit(store?.steps.find((step) => step.screen === ScreenType.SELECT_ORDER_DELIVERY_LOCATION && step.trigger === ScreenTrigger.SUBMIT));
        } finally {
            setIsSubmitting(false);
        }
    }, [
        navigationState.user,
        onSubmit,
        selectedAmount,
        selectedProduct,
        selectedSpot,
        store?.functionalitySettings,
        store?.steps,
        store?.wizard.account_id,
        updateWizardData
    ]);

    return {
        selectedProduct,
        currentSpot,
        defaultSpot,
        selectedSpot,
        selectedAnotherSpot,
        selectedDeliveryOption,
        isLoadingDefaultSpot,
        isSubmitting,
        onBack,
        selectCurrentSpot,
        selectDefaultSpot,
        selectAnotherSpot,
        confirmOrder
    };
};
